import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Mail, User, Save, Trash2 } from "lucide-react";
import { useSession } from "@/App";

// CANONICAL WHITE WORDMARK
const WORDMARK_URL = "/havn-logo-white.png";
const BG_IMAGE_URL = "/assets/havn_bg_dark.png";

// Background style with soft glow and vignette
const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(${BG_IMAGE_URL})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

export default function CaregiverPage() {
  const navigate = useNavigate();
  const { caregiverStorage, sessionLogger } = useSession();
  
  const [caregiver, setCaregiver] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load caregiver on mount
  useEffect(() => {
    const stored = caregiverStorage.get();
    if (stored) {
      setCaregiver(stored);
      setName(stored.name || '');
      setPhone(stored.phone || '');
      setEmail(stored.email || '');
    } else {
      setIsEditing(true);
    }
  }, [caregiverStorage]);

  const handleSave = () => {
    if (!name.trim()) return;
    
    const newCaregiver = {
      name: name.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      updatedAt: new Date().toISOString()
    };
    
    caregiverStorage.save(newCaregiver);
    setCaregiver(newCaregiver);
    setIsEditing(false);
    setSaved(true);
    sessionLogger.log('caregiver_saved', { name: newCaregiver.name });
    
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDelete = () => {
    caregiverStorage.clear();
    setCaregiver(null);
    setName('');
    setPhone('');
    setEmail('');
    setIsEditing(true);
    sessionLogger.log('caregiver_deleted');
  };

  const handleCall = () => {
    if (caregiver?.phone) {
      sessionLogger.log('caregiver_call_initiated', { phone: caregiver.phone });
      window.location.href = `tel:${caregiver.phone}`;
    }
  };

  const handleEmail = () => {
    if (caregiver?.email) {
      sessionLogger.log('caregiver_email_initiated', { email: caregiver.email });
      window.location.href = `mailto:${caregiver.email}`;
    }
  };

  return (
    <div className="havn-container" style={BG_STYLE} data-testid="caregiver-page">
      <div className="havn-bg-gradient" aria-hidden="true" />
      
      {/* Header */}
      <header style={{
        position: 'absolute',
        top: '20px',
        left: '0',
        right: '0',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxSizing: 'border-box',
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          aria-label="Go back"
          data-testid="back-button"
        >
          <ArrowLeft size={24} className="text-[var(--sidekick-text-secondary)]" />
        </button>
        
        <img 
          src="/havn-logo-white.png"
          alt="HAVN"
          style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
        />
        
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      <main className="flex flex-col items-center justify-center flex-1 z-10 px-6 w-full max-w-md mx-auto gap-8">
        {/* Title */}
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-semibold text-[var(--sidekick-text-primary)] mb-2">
            Trusted Contact
          </h1>
          <p className="text-[var(--sidekick-text-secondary)]">
            Someone you can reach for help
          </p>
        </div>

        {/* Form / Display */}
        <motion.div
          className="w-full sk-glass-card p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {isEditing ? (
            <div className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm text-[var(--sidekick-text-secondary)] mb-2">
                  Name *
                </label>
                <div className="relative">
                  <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sidekick-text-secondary)]" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., John (my son)"
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-xl
                             pl-10 pr-4 py-3 text-[var(--sidekick-text-primary)] placeholder:text-[var(--sidekick-text-secondary)]
                             focus:outline-none focus:border-[var(--sidekick-primary)]"
                    data-testid="caregiver-name-input"
                  />
                </div>
              </div>

              {/* Phone Input */}
              <div>
                <label className="block text-sm text-[var(--sidekick-text-secondary)] mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sidekick-text-secondary)]" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g., 555-123-4567"
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-xl
                             pl-10 pr-4 py-3 text-[var(--sidekick-text-primary)] placeholder:text-[var(--sidekick-text-secondary)]
                             focus:outline-none focus:border-[var(--sidekick-primary)]"
                    data-testid="caregiver-phone-input"
                  />
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm text-[var(--sidekick-text-secondary)] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sidekick-text-secondary)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g., john@email.com"
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-xl
                             pl-10 pr-4 py-3 text-[var(--sidekick-text-primary)] placeholder:text-[var(--sidekick-text-secondary)]
                             focus:outline-none focus:border-[var(--sidekick-primary)]"
                    data-testid="caregiver-email-input"
                  />
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={!name.trim()}
                className="w-full py-4 bg-[var(--sidekick-primary)] text-[var(--sidekick-primary-foreground)]
                         font-semibold rounded-xl flex items-center justify-center gap-2
                         hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all"
                data-testid="save-caregiver-button"
              >
                <Save size={20} />
                Save Contact
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Display Mode */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-[var(--sidekick-primary)] bg-opacity-20 
                              flex items-center justify-center mx-auto mb-4">
                  <User size={40} className="text-[var(--sidekick-primary)]" />
                </div>
                <h2 className="text-xl font-semibold text-[var(--sidekick-text-primary)]">
                  {caregiver?.name}
                </h2>
              </div>

              {/* Contact Actions */}
              <div className="space-y-3">
                {caregiver?.phone && (
                  <button
                    onClick={handleCall}
                    className="w-full py-4 bg-[var(--sidekick-primary)] text-[var(--sidekick-primary-foreground)]
                             font-semibold rounded-xl flex items-center justify-center gap-3
                             hover:opacity-90 transition-all text-lg"
                    data-testid="call-caregiver-button"
                  >
                    <Phone size={24} />
                    Call {caregiver.name}
                  </button>
                )}
                
                {caregiver?.email && (
                  <button
                    onClick={handleEmail}
                    className="w-full py-4 bg-transparent border border-[var(--sidekick-primary)]
                             text-[var(--sidekick-primary)] font-semibold rounded-xl flex items-center justify-center gap-3
                             hover:bg-[rgba(var(--sk-primary-rgb),0.1)] transition-all"
                    data-testid="email-caregiver-button"
                  >
                    <Mail size={22} />
                    Email {caregiver.name}
                  </button>
                )}
              </div>

              {/* Edit / Delete */}
              <div className="flex gap-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 py-3 text-[var(--sidekick-text-secondary)] hover:text-[var(--sidekick-text-primary)]
                           transition-colors"
                  data-testid="edit-caregiver-button"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-3 text-red-400 hover:text-red-300 flex items-center justify-center gap-2
                           transition-colors"
                  data-testid="delete-caregiver-button"
                >
                  <Trash2 size={18} />
                  Remove
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Saved indicator */}
        {saved && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[var(--sidekick-primary)] text-sm"
          >
            Contact saved!
          </motion.p>
        )}
      </main>
    </div>
  );
}
